import type { GeneralInfo } from "@/contexts/WizardContext";

export interface ValidationIssue {
  field: string;
  message: string;
  severity: "error" | "warning";
}

/**
 * Validates all General Information fields according to business rules
 */
export function validateGeneralInfo(generalInfo: GeneralInfo): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // 1. Primary site address validation (first site in siteAddresses array)
  const siteAddresses = generalInfo.siteAddresses || [];
  const primarySite = siteAddresses[0];
  const hasPrimarySiteAddress = primarySite?.address?.trim() || primarySite?.coords;
  
  if (!hasPrimarySiteAddress) {
    issues.push({
      field: "siteAddresses",
      message: "Primary site address: Must provide an address and/or coordinates",
      severity: "error"
    });
  }

  // 2. Number of sites validation - ensure numberOfSites matches array length expectation
  const numberOfSitesValue = generalInfo.numberOfSites || "1";
  const expectedSiteCount = numberOfSitesValue === "5+" ? 5 : parseInt(numberOfSitesValue);
  
  if (siteAddresses.length < expectedSiteCount) {
    issues.push({
      field: "numberOfSites",
      message: `Number of sites is set to ${numberOfSitesValue}, but only ${siteAddresses.length} site(s) configured`,
      severity: "warning"
    });
  }

  // 3. Net turnover validation
  const netTurnover = parseFloat(generalInfo.netTurnover || "0");
  if (netTurnover < 0) {
    issues.push({
      field: "netTurnover",
      message: "Net turnover must be greater than or equal to 0",
      severity: "error"
    });
  }

  // 4. Balance sheet total validation
  const balanceSheetTotal = parseFloat(generalInfo.balanceSheetTotal || "0");
  if (balanceSheetTotal < 0) {
    issues.push({
      field: "balanceSheetTotal",
      message: "Balance sheet total must be greater than or equal to 0",
      severity: "error"
    });
  }

  // Balance sheet should be >= net turnover for most SMEs
  if (balanceSheetTotal > 0 && netTurnover > 0 && balanceSheetTotal < netTurnover) {
    issues.push({
      field: "balanceSheetTotal",
      message: `Balance sheet total (${balanceSheetTotal}) is lower than net turnover (${netTurnover}). Please verify these values.`,
      severity: "warning"
    });
  }

  // 5. Total employees validation
  const totalEmployees = parseFloat(generalInfo.totalEmployees || "0");
  const maleEmployees = parseFloat(generalInfo.maleEmployees || "0");
  const femaleEmployees = parseFloat(generalInfo.femaleEmployees || "0");

  if (totalEmployees < 0) {
    issues.push({
      field: "totalEmployees",
      message: "Total employees must be a non-negative number",
      severity: "error"
    });
  }

  if (maleEmployees < 0) {
    issues.push({
      field: "maleEmployees",
      message: "Male employees must be non-negative",
      severity: "error"
    });
  }

  if (femaleEmployees < 0) {
    issues.push({
      field: "femaleEmployees",
      message: "Female employees must be non-negative",
      severity: "error"
    });
  }

  // Cross-check employee totals - Male + Female must equal Total exactly
  const sumGender = maleEmployees + femaleEmployees;

  if (sumGender > 0 && totalEmployees > 0 && sumGender !== totalEmployees) {
    issues.push({
      field: "totalEmployees",
      message: `Total employees (${totalEmployees}) should equal total males (${maleEmployees}) plus total females (${femaleEmployees})`,
      severity: "error"
    });
  }

  const homeworkingPercent = parseFloat(generalInfo.homeworkingPercent || "0");
  if (homeworkingPercent < 0 || homeworkingPercent > 100) {
    issues.push({
      field: "homeworkingPercent",
      message: "Homeworking percentage must be between 0 and 100. Please enter a valid number.",
      severity: "error"
    });
  }

  return issues;
}

/**
 * Count validation issues by severity
 */
export function countIssues(issues: ValidationIssue[]): { errors: number; warnings: number } {
  return {
    errors: issues.filter(i => i.severity === "error").length,
    warnings: issues.filter(i => i.severity === "warning").length
  };
}
